import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Icon from '@/components/ui/icon';
import {
  getNavigationItemsForRole,
  type FlatNavigationItem,
} from '@/constants/navigation';
import { useNavigationFavorites } from '@/hooks/useNavigationFavorites';
import { useAuth } from '@/hooks/useAuth';
import { normalizeUserRole } from '@/types/user';

type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate?: (path: string) => void;
};

const isTextInput = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'textbox'
  );
};

const getDescription = (item: FlatNavigationItem) =>
  item.parents.length > 0 ? item.parents.join(' / ') : undefined;

export default function CommandPalette({
  open,
  onOpenChange,
  onNavigate,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const { favorites, toggleFavorite } = useNavigationFavorites();
  const user = useAuth();
  const userRole = normalizeUserRole(user?.role);

  const favoritesSet = useMemo(() => new Set(favorites), [favorites]);

  const accessibleItems = useMemo(
    () => getNavigationItemsForRole(userRole),
    [userRole],
  );

  const favoriteEntries = useMemo(
    () => accessibleItems.filter((item) => favoritesSet.has(item.path)),
    [accessibleItems, favoritesSet],
  );

  const otherEntries = useMemo(
    () => accessibleItems.filter((item) => !favoritesSet.has(item.path)),
    [accessibleItems, favoritesSet],
  );

  const normalizedQuery = search.trim().toLowerCase();

  const matchesQuery = useCallback(
    (item: FlatNavigationItem) => {
      if (!normalizedQuery) {
        return true;
      }

      return (
        item.label.toLowerCase().includes(normalizedQuery) ||
        item.fullLabel.toLowerCase().includes(normalizedQuery) ||
        item.path.toLowerCase().includes(normalizedQuery)
      );
    },
    [normalizedQuery],
  );

  const filteredFavorites = useMemo(
    () => favoriteEntries.filter((item) => matchesQuery(item)),
    [favoriteEntries, matchesQuery],
  );

  const filteredOtherItems = useMemo(
    () => otherEntries.filter((item) => matchesQuery(item)),
    [otherEntries, matchesQuery],
  );

  const hasResults =
    filteredFavorites.length > 0 || filteredOtherItems.length > 0;

  const handleSelect = useCallback(
    (path: string) => {
      onOpenChange(false);
      setSearch('');
      navigate(path);
      onNavigate?.(path);
    },
    [navigate, onNavigate, onOpenChange],
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      if (
        (event.key === 'k' || event.key === 'K') &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        onOpenChange(true);
        return;
      }

      if (
        event.key === '/' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isTextInput(event.target)
      ) {
        event.preventDefault();
        onOpenChange(true);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }

    const focusInput = () => {
      inputRef.current?.focus();
    };

    if (typeof window.requestAnimationFrame === 'function') {
      const frame = window.requestAnimationFrame(focusInput);
      return () => window.cancelAnimationFrame(frame);
    }

    const timeout = window.setTimeout(focusInput, 0);
    return () => window.clearTimeout(timeout);
  }, [open]);

  const renderItem = useCallback(
    (item: FlatNavigationItem) => {
      const isFavorite = favoritesSet.has(item.path);
      const description = getDescription(item);

      return (
        <CommandItem
          key={item.path}
          value={item.fullLabel}
          onSelect={() => handleSelect(item.path)}
          className="w-full"
        >
          <div className="flex w-full items-center gap-3">
            {item.icon ? (
              <Icon
                name={item.icon}
                className="h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
            ) : (
              <span className="h-4 w-4" aria-hidden="true" />
            )}
            <div className="flex flex-1 flex-col text-left">
              <span className="font-medium">{item.label}</span>
              {description ? (
                <span className="text-xs text-muted-foreground">
                  {description}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-muted-foreground sm:inline">
                {item.path}
              </span>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  toggleFavorite(item.path);
                }}
                className="rounded p-1 text-muted-foreground transition hover:text-yellow-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={
                  isFavorite
                    ? `Ta bort ${item.label} från favoriter`
                    : `Lägg till ${item.label} i favoriter`
                }
              >
                <Icon
                  name={isFavorite ? 'Star' : 'StarOff'}
                  size={18}
                  className={isFavorite ? 'text-yellow-400' : undefined}
                  fill={isFavorite ? 'currentColor' : 'none'}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </CommandItem>
      );
    },
    [favoritesSet, handleSelect, toggleFavorite],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] overflow-hidden p-0"
        aria-label="Kommandopalett"
      >
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="text-sm font-semibold text-foreground">
            Snabbnavigering
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Använd Ctrl/Cmd + K eller / för att öppna paletten.
          </DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false} className="bg-background">
          <CommandInput
            ref={inputRef}
            placeholder="Sök efter sida..."
            value={search}
            onValueChange={setSearch}
            aria-label="Sök i navigering"
          />
          <CommandList>
            {hasResults ? (
              <>
                {filteredFavorites.length > 0 ? (
                  <CommandGroup heading="Favoriter">
                    {filteredFavorites.map(renderItem)}
                  </CommandGroup>
                ) : null}
                {filteredOtherItems.length > 0 ? (
                  <CommandGroup heading="Navigering">
                    {filteredOtherItems.map(renderItem)}
                  </CommandGroup>
                ) : null}
              </>
            ) : (
              <CommandEmpty>Inga träffar</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

